import { Product } from "@design-controls/types";
import { useCurrentUser, useToastProvider } from "@qualio/ui-components";
import { useMutation, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { designControlsApi } from "../../api/designControls";

type UseProducts = {
  isLoading: boolean;
  product: Product | undefined;
  products: Array<Product>;
  deleteProduct: (productId: string, options?: any) => void;
  isDeleting: boolean;
};

type UseProductsProps = {
  productId?: string;
};

export const useProducts = (options?: UseProductsProps): UseProducts => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { showToast } = useToastProvider();
  const {
    isLoading,
    data: products,
    refetch,
    isRefetching,
  } = useQuery(`query-${companyId}-products-list`, () =>
    designControlsApi.retrieveProductsByCompany(companyId).then((response) => {
      return [...response].sort((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
      );
    }),
  );

  let product: Product | undefined = undefined;
  if (!isLoading && options?.productId && products) {
    product = products.find((product) => product.id === options?.productId);
  }

  const { mutate: deleteProduct, isLoading: isDeleting } = useMutation(
    (productId: string) =>
      designControlsApi.deleteProduct(companyId, productId),
    {
      onSuccess: (data, productId) => {
        const product = (products as Array<Product>).find(
          (item) => item.id === productId,
        ) as Product;
        showToast({
          title: t("products.notifications.deleteSuccess.title"),
          description: t("products.notifications.deleteSuccess.description", {
            productName: product.name,
          }),
          status: "success",
          duration: 5000,
        });
        return refetch();
      },
      onError: (data: any, productId) => {
        const product = (products as Array<Product>).find(
          (item) => item.id === productId,
        ) as Product;
        showToast({
          title: t("products.notifications.deleteError.title"),
          description: t("products.notifications.deleteError.description", {
            productName: product.name,
          }),
          status: "error",
          duration: 5000,
        });
      },
    },
  );

  return {
    isLoading: isRefetching || isLoading,
    product,
    products: products || [],
    deleteProduct,
    isDeleting,
  };
};
